import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    OnInit,
    ViewChild, ViewEncapsulation
} from '@angular/core';
import {IrbEditor} from "@irb/irb-editors/irb-editors.base";
import {IEvent, IrbAppEvents, IrbEditorsEvents, IrbModelsEvents} from "@irb-common/events";
import {DataTransferService} from "@irb-common/services/data-transfer.service";
import {ISecurityCustomCategory} from "@irb/irb-models/irb-models.model";
import {IAlternativeSecurity} from "@irb/irb-editors/irb-editors.model";
import {HttpErrorResponse} from "@angular/common/http";
import {Observable, of} from "rxjs";
import {SecurityAssetCategoryComponent} from "@irb/irb-editors/editor-security/security-asset-category/security-asset-category.component";
import {TradingMinimumsComponent} from "@irb/irb-editors/editor-security/trading-minimums/trading-minimums.component";
import {SecurityPenaltybarComponent} from "@irb/irb-editors/editor-security/security-penaltybar/security-penaltybar.component";
import {SecurityRestrictionsComponent} from "@irb/irb-editors/editor-security/security-restrictions/security-restrictions.component";
import {SecurityEquivalentsAlternativesComponent} from "@irb/irb-editors/editor-security/security-equivalents-alternatives/security-equivalents-alternatives.component";
import {HouseholdLocationPreferencesComponent} from '@irb/irb-editors/editor-security/household-location-preferences/household-location-preferences.component';
import {IAlertDialogData} from "@irb/app.model";
import {IDropdownItem} from '@irb-common/models/dropdown.model';
import {
    TlhWashSaleAlternativesComponent
} from "@irb/irb-editors/editor-security/tlh-wash-sale-alternatives/tlh-wash-sale-alternatives.component";

@Component({ standalone: false,
    selector: 'irb-editor-multiple-security',
    templateUrl: './edit-multiple-security.component.html',
    styleUrls: ['./edit-multiple-security.component.scss', "../../irb-common/styles/dialog.scss", "../editor-sidebar/editor-sidebar.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.None
})
export class EditorMultipleSecurityComponent extends IrbEditor {
    @ViewChild("assetCategoryComponent") assetCategoryComponent: SecurityAssetCategoryComponent;
    @ViewChild("tradingMinimumsComponent") tradingMinimumsComponent: TradingMinimumsComponent
    @ViewChild("securityPenaltybarComponent") securityPenaltybarComponent: SecurityPenaltybarComponent;
    @ViewChild("securityRestrictionsComponent") securityRestrictionsComponent: SecurityRestrictionsComponent;
    @ViewChild("securityEquivalentsAlternativesComponent") securityEquivalentsAlternativesComponent: SecurityEquivalentsAlternativesComponent;
    @ViewChild("householdLocationPreferencesComponent") householdLocationPreferencesComponent: HouseholdLocationPreferencesComponent;
    @ViewChild("tLHWashSaleAlternativesComponent") tLHWashSaleAlternativesComponent: TlhWashSaleAlternativesComponent;


    @ViewChild("body") body: ElementRef;
    _collapseForOpenTriggers: boolean;
    securityIds: Array<number>;
    _tradingMinimumData: { multipleSecurity: boolean, isModelSecurity: boolean, securityData: any[] };
    defaultExpandedPanels: Array<string> = ["ASSET_CATEGORY","TRADING_MINIMUMS", "PENALTIES",
        "RESTRICTIONS", "EQUIVALENTS_ALTERNATIVES", "TLH_WASH_SALE_ALTERNATIVES", "HOUSEHOLD_LOCATION_PREFERENCES"];
    symbol: string;
    symbolDescription: string;
    modifiedDate: any;
    securityTypeDesc: string;
    isUiTest = false;
    saveMultiSecThreshold = 500;
    updateStatusCount = 0;
    progressTimer: any = null;
    isTlhWsaEnabled: boolean = false;

    constructor(private cdr: ChangeDetectorRef, public dataTransferService: DataTransferService) {
        super();
        this.identifier = "ID";
        this.getUserDetails();
    }

    getUserDetails(): void {
        this.as.getUserDetails().subscribe((res: any) => {
            if (res) {
                this.saveMultiSecThreshold = res.saveMultiSecThreshold || 500;
                this.isTlhWsaEnabled = res.isTlhWsaEnabled;
            }
        });
    }

    initExpansionPanels() {
        let dep = this.defaultExpandedPanels;
        let sessionOverridesDefault = true;
        // modify if the targeted panel need to be expanded and all other needs to be collapsed.
        this.ies.loadExpansionState(dep.length, 'editor-multiple-security', dep, sessionOverridesDefault);
    }

    get(event: IEvent) {
        super.get(event);
        this.initExpansionPanels();
        if (event.data && event.data.data.securityIds && event.data.data.securityIds.length > 1) {
            this.securityIds = event.data.data.securityIds;
            this.getMultipleSecurityDetail(this.securityIds);
        }
    }

    getMultipleSecurityDetail(securityIds: any) {
        this.ies.getMultipleSecurityDetail(securityIds).subscribe((data: any) => {
            // data.ID = this.securityIds.join("");
            let dataObj = {
                ID: this.securityIds.join(""),
                securityData: [...data]
            };
            this.load(dataObj);
        });
    }

    load(data: any) {
        super.load(data);
        this.symbol = this.getInitialValue('securitySymbol', data?.securityData);
        this.symbolDescription = this.getInitialValue('securityDescription', data?.securityData);
        this.modifiedDate = this.getInitialValue('modifiedDate', data?.securityData);
        this.securityTypeDesc = this.formatSecurityType(this.getInitialValue('securityType', data?.securityData));

        this.setTradeMinimumData(data?.securityData);
        this.cdr.detectChanges();
        setTimeout(() => this.onResize());
    }

    private setTradeMinimumData(data: any) {
        this._tradingMinimumData = {multipleSecurity: true, isModelSecurity: false, securityData: [...data]};
    }

    @HostListener("window:resize")
    onResize() {
        if (this.body) {
            const rect = this.body.nativeElement.getBoundingClientRect();
            let h = (window.innerHeight - rect.top - 10);

            h = (h < 200) ? 200 : h;
            this.bodyHeight = h + "px";
        }
    }

    get collapseForOpenTriggers() {
        return this._collapseForOpenTriggers;
    }

    onCustomAssetCategoryChanged(selectedCustomCategory: IDropdownItem) {
        if (this.originalData && this.originalData.securityData && this.originalData.securityData.length > 0) {
            for (let i = 0; i < this.data.securityData.length; i++) {
                this.data.securityData[i].customCategoryName = selectedCustomCategory.name;
                this.data.securityData[i].customCategoryId = selectedCustomCategory.value;
            }
            this.onInputChange();
        }
    }

    onTradingMinimumsChanged(event: { key: string, data: any }) {
        if (this.originalData && this.originalData.securityData && this.originalData.securityData.length > 0) {
            for (let i = 0; i < this.data.securityData.length; i++) {
                this.data.securityData[i][event.key] = event.data;
            }
            this.onInputChange();
        }

    }

    onTradingMinimumAlternateSecurityChanged(event: IDropdownItem) {
        if (this.originalData && this.originalData.securityData && this.originalData.securityData.length > 0) {
            for (let i = 0; i < this.data.securityData.length; i++) {
                if (Number(event.value) === 0) {
                    this.data.securityData[i].tradeMinimumAlternative = null;
                    this.data.securityData[i].tradeMinimumAlternativeSymb = null;
                } else {
                    this.data.securityData[i].tradeMinimumAlternative = Number(event.value);
                    this.data.securityData[i].tradeMinimumAlternativeSymb = event.name;
                    }
            }
            this.onInputChange();
        }
    }

    // onExternalSharePriceChange(event: number) {
    //     if (this.originalData && this.originalData.length > 0) {
    //         for (let i = 0; i < this.data.length; i++) {
    //             this.data[i].externalSharePrice = event;
    //         }
    //         this.onInputChange();
    //     }
    // }

    private getInitialValue(name: string, items: any[]) {
        let inputVal = items[0][name];
        for (let i = 0; i < items.length; i++) {
            let thisVal = items[i][name];
            if (inputVal !== thisVal) {
                inputVal = "Various";
            }
        }
        return inputVal;
    }

    onSecurityRestrictionChanged(event: { sellingTreatment: any, conditions: any, altSecurity: IDropdownItem }) {
        if (this.originalData && this.originalData.securityData && this.originalData.securityData.length > 0) {
            for (let i = 0; i < this.data.securityData.length; i++) {
                this.data.securityData[i].sellingTreatment = event.sellingTreatment;
                this.data.securityData[i].conditions = event.conditions;
                if (event.altSecurity) {
                    this.data.securityData[i].altSecurityId = event.altSecurity.value;
                    this.data.securityData[i].altSecuritySymbol = event.altSecurity.name;
                } else {
                    this.data.securityData[i].altSecurityId = null;
                    this.data.securityData[i].altSecuritySymbol = null;
                }
            }
        }
        this.onInputChange();
    }

    onRothLevelChanged(event: Number) {
        if (this.originalData && this.originalData.securityData && this.originalData.securityData.length > 0) {
            for (let i = 0; i < this.data.securityData.length; i++) {
                this.data.securityData[i].rothLevel = event;
            }
            this.onInputChange();
        }
    }

    onLevelChanged(event: Number) {
        if (this.originalData && this.originalData.securityData && this.originalData.securityData.length > 0) {
            for (let i = 0; i < this.data.securityData.length; i++) {
                this.data.securityData[i].taxabilityBias = event;
            }
            this.onInputChange();
        }
    }

    formatSecurityType(securityType: any) {

        switch (securityType) {

            case "EQUITY":
                return "Equity";
            case "EQ":
                return "Equity";
            case "MUTUAL_FUND":
                return "Mutual Fund";
            case "MF":
                return "Mutual Fund";
            case "CASH":
                return "Cash";
            case "GROUP":
                return "Group";
            case "BOND":
                return "Bond";
            case "OTHER":
                return "Other";
            case "EXTERNAL":
                return "External";
            case "CustCat":
                return "Category";
            case "OPTION":
                return "Option";
            default:
                return securityType;
        }
    }

    onSecurityEquivalentsAlternativeChanged(event: { alternativeSecurity: IAlternativeSecurity, inverseAccountType: string }) {
        if (this.originalData && this.originalData.securityData && this.originalData.securityData.length > 0) {
            for (let i = 0; i < this.data.securityData.length; i++) {
                if (event.alternativeSecurity && event.alternativeSecurity.securityId > 0) {
                    let altSymbol = (event.inverseAccountType === "QUALIFIED" ? "qualifiedAlternativeSymbol" : "taxableAlternativeSymbol");
                    let negatedAltSymbol = (event.inverseAccountType === "QUALIFIED" ? "taxableAlternativeSymbol" : "qualifiedAlternativeSymbol");
                    this.data.securityData[i][altSymbol] = event.alternativeSecurity.securityId;
                    this.data.securityData[i][negatedAltSymbol] = null;
                    this.data.securityData[i]['taxQualAltSecSymb'] = event.alternativeSecurity.securitySymbol;
                } else {
                    this.data.securityData[i].qualifiedAlternativeSymbol = null;
                    this.data.securityData[i].taxableAlternativeSymbol = null;
                    this.data.securityData[i]['taxQualAltSecSymb'] = null;
                }
            }
            this.onInputChange();
        }
    }

    isVisible(componentId: string) {
        if (!this._isLoading && this._tradingMinimumData && this._tradingMinimumData.securityData) {
            switch (componentId) {
                case 'ASSET_CATEGORY':
                    return true;
                case 'TRADING_MINIMUMS':
                    return true;
                case 'PENALTIES':
                    return true;
                case 'RESTRICTIONS':
                    return true;
                case 'EQUIVALENTS_ALTERNATIVES':
                    return true;
                case 'TLH_WASH_SALE_ALTERNATIVES':
                    return true;
                case 'HOUSEHOLD_LOCATION_PREFERENCES':
                    return true;
                case 'EXTERNAL_HOLDINGS':
                    return false;
            }
        } else {
            return false;
        }

    }

    getSecurityItemsChange() {
        let securityItemsToSave: any[] = [];
        if (this.data && this.data.securityData) {
            this.data.securityData.forEach((x: any) => {
                securityItemsToSave.push(this.getSecurityChangedItem(x));
            });
        }
        return securityItemsToSave;
    }

    getSecurityChangedItem(security: any) {
        let saveObject = this.util.clone(security);
        let isMinBuyChanged = false, isMinSelChanged = false, isMinBuyPercentChanged = false,
            isMinSelPercentChanged = false;
        let advisorImposedBuyMinVal = this.dataTransferService.advisorImposedBuyMin ? this.dataTransferService.advisorImposedBuyMin : 0;
        let advisorImposedSellMinVal = this.dataTransferService.advisorImposedSellMin ? this.dataTransferService.advisorImposedSellMin : 0;
        let advisorImposedBuyPerMinVal = this.dataTransferService.advisorImposedBuyMinPercent ? this.dataTransferService.advisorImposedBuyMinPercent : 0;
        let advisorImposedSellPerMinVal = this.dataTransferService.advisorImposedSellMinPercent ? this.dataTransferService.advisorImposedSellMinPercent : 0;

        saveObject["isInlineEdit"] = 0;
        // set minChanged Flags.
        if (saveObject['buyMinimum'] !== advisorImposedBuyMinVal) {
            isMinBuyChanged = true;
        }
        if (saveObject['sellMinimum'] !== advisorImposedSellMinVal) {
            isMinSelChanged = true;
        }
        if (saveObject['buyMinimumPercent'] !== advisorImposedBuyPerMinVal) {
            isMinBuyPercentChanged = true;
        }
        if (saveObject['sellMinimumPercent'] !== advisorImposedSellPerMinVal) {
            isMinSelPercentChanged = true;
        }
        saveObject['isMinBuyChanged'] = isMinBuyChanged;
        saveObject['isMinSelChanged'] = isMinSelChanged;
        saveObject['isMinBuyPercentChanged'] = isMinBuyPercentChanged;
        saveObject['isMinSelPercentChanged'] = isMinSelPercentChanged;

        //this.data.qualifiedAlternativeSymbol = null;
        //this.data.taxableAlternativeSymbol = null;
        if(!saveObject.qualifiedAlternativeSymbol) {
            saveObject.qualifiedAlternativeSymbol = 0;
        }
        if(!saveObject.taxableAlternativeSymbol) {
            saveObject.taxableAlternativeSymbol = 0;
        }
        if(!saveObject.tradeMinimumAlternative) {
            saveObject.tradeMinimumAlternative = 0;
        }

        if(saveObject.taxableAlternativeSymbol  && saveObject.taxableAlternativeSymbol > 0) {
            saveObject.qualifiedAlternativeSymbol = null;
        }
        if(saveObject.qualifiedAlternativeSymbol  && saveObject.qualifiedAlternativeSymbol > 0) {
            saveObject.taxableAlternativeSymbol = null;
        }

        return saveObject;
    }

    save() {
        this.validateInput().subscribe(errors => {
            if (errors.length > 0) {
                let errMsg: string = errors.join("\n");
                // console.log(errMsg);
                this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
                    dTitle: "Alert",
                    msg: errMsg,
                    dProceedBtnLabel: "Ok",
                    dType: "ERROR"
                } as IAlertDialogData);
            } else {
                this.as.fireEvent(IrbEditorsEvents.SAVE_START, {"slot": this.slot});
                let securitiesToSave = this.getSecurityItemsChange();
                // save for the edit security screen.

                if (this.securityIds.length > this.saveMultiSecThreshold) {
                    this.ies.saveMultipleSecurityProfilesAsync(securitiesToSave).subscribe((response: any) => {
                        this.updateStatusCount = 0;
                        if(this.progressTimer) clearTimeout(this.progressTimer);
                        this.actionStatusCheckIntervalStart(response.responseUtilityMap.statusId);
                    }, () => {
                        this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT, {"slot": this.slot});
                    });
                } else {
                    this.ies.saveMultipleSecurityProfile(securitiesToSave).subscribe((response: any) => {
                            // console.log('saveMultipleSecurityProfile', response);
                            this.saveMultipleSecuritiesSuccess(response);
                        },
                        (_err: HttpErrorResponse) => {
                            this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {'slot': this.slot});
                        });
                }
            }
        });
    }

    saveMultipleSecuritiesSuccess(response: any) {
        if (response.responseStatus === "SUCCESS") {
            if (response.responseData != null) {
                this.securityIds = this.securityIds;
                this.as.fireEvent(IrbEditorsEvents.SAVE_SUCCESS, {
                    "hasPendingFunction": this.hasPendingFunction,
                    "data": "Securities updated.",
                    "slot": this.slot
                });
                // let dataObj = {
                //     ID: this.securityIds.join(""),
                //     securityData: [...response.responseData]
                // };
                // this.load(dataObj);

            }
            this.as.fireEvent(IrbEditorsEvents.CLOSE_WITHOUT_SAVE, {"slot": this.slot});
            this.hasPendingFunction = false;
            this.as.fireEvent(IrbAppEvents.GRID_REFRESH, {"slot": this.slot});
        } else if (response.responseStatus === "BUSINESS_ERR_MSG" || response.responseStatus === "EXCEPTION") {
            this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT, {"slot": this.slot});
            this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                severity: "error",
                summary: "Please refresh Page",
                detail: "Exception occurred",
            });
            if (!this.isUiTest) {
                throw new Error('Edit saveMultipleSecurityProfile failed.');
            }

        } else if (response.responseStatus === 'STALEDATAEXCEPTION') {
            this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT, {"slot": this.slot});
            this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
                dTitle: 'Refresh Required',
                dProceedBtnLabel: 'Refresh',
                msg: "You are attempting to edit items which were just modified by someone else. To see the updated values, you need to refresh the page.",
                onProceed: () => {
                    this.ies.getMultipleSecurityDetail(this.securityIds).subscribe((data: any) => {
                        let dataObj = {
                            ID: this.securityIds.join(""),
                            securityData: [...data]
                        };
                        this.load(dataObj);
                        this.cdr.detectChanges();
                    });
                },
                onCancel: () => {
                    this.revert();
                }

            } as IAlertDialogData);
        }
    }

    areValidAllComps() {
        let valid = true;
        if (this.isVisible('ASSET_CATEGORY') && this.assetCategoryComponent) {
            valid = valid && this.assetCategoryComponent.isValid();
        }

        if (this.isVisible('TRADING_MINIMUMS') && this.tradingMinimumsComponent) {
            valid = valid && this.tradingMinimumsComponent.isValid();
        }

        if (this.isVisible('PENALTIES') && this.securityPenaltybarComponent) {
            valid = valid && this.securityPenaltybarComponent.isValid();
        }

        if (this.isVisible('RESTRICTIONS') && this.securityRestrictionsComponent) {
            valid = valid && this.securityRestrictionsComponent.isValid();
        }

        if (this.isVisible('EQUIVALENTS_ALTERNATIVES') && this.securityEquivalentsAlternativesComponent) {
            valid = valid && this.securityEquivalentsAlternativesComponent.isValid();
        }

        if (this.isVisible('HOUSEHOLD_LOCATION_PREFERENCES') && this.householdLocationPreferencesComponent) {
            valid = valid && this.householdLocationPreferencesComponent.isValid();
        }

        if (this.isVisible('TLH_WASH_SALE_ALTERNATIVES') && this.tLHWashSaleAlternativesComponent) {
            valid = valid && this.tLHWashSaleAlternativesComponent.isValid();
        }

        return valid;
    }

    validateInput(): Observable<string[]> {
        const errors: string[] = [];
        if(!this.areValidAllComps()) {
            errors.push('Securities cannot be saved. Please complete all the required fields.')
        }
        return of(errors);
    }


    updateProgressBarStatus(response: any) {
        let timeout = (this.updateStatusCount > 10) ? 8000 : (this.updateStatusCount > 2) ? 3000 : 1500;
        if (this.isUiTest)
            timeout = 0;
        this.updateStatusCount++;
        this.progressTimer = setTimeout(() => {
            this.actionStatusCheckIntervalStart(response.statusId);
        }, timeout);
    }

    actionStatusCheckIntervalStart(statusId: any) {
        this.ies.actionStatusCheck(statusId).subscribe((statusResponse: any) => {
            if (statusResponse.responseStatus === "SUCCESS") {
                const response = statusResponse.responseData;
                const reqStatus = response.reqStatus.trim().toUpperCase();
                if (reqStatus === 'FAILED') {
                    if (response.failMsg && response.failMsg.toUpperCase() === 'STALEDATAEXCEPTION')
                        this.showStaleDataDialog();
                    else
                        this.actionStatusCheckFailed();

                    this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT, {"slot": this.slot});
                }

                if (reqStatus === 'STARTED' || reqStatus === 'NEW' || reqStatus === 'IN_PROGRESS') {
                    this.updateProgressBarStatus(response);
                }
                if (reqStatus === 'COMPLETED') {
                    this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT, {"slot": this.slot});
                    if (this.progressTimer) clearTimeout(this.progressTimer);
                    this.as.fireEvent(IrbEditorsEvents.SAVE_SUCCESS, {
                        "hasPendingFunction": this.hasPendingFunction,
                        "data": "Securities updated.",
                        "slot": this.slot
                    });
                    //this.getMultipleSecurityDetail(this.securityIds);
                    this.as.fireEvent(IrbEditorsEvents.CLOSE_WITHOUT_SAVE, {"slot": this.slot});
                    this.as.fireEvent(IrbAppEvents.GRID_REFRESH, {"slot": this.slot});
                }
            } else {
                this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT, {"slot": this.slot});
                if (this.progressTimer) clearTimeout(this.progressTimer);
            }
        }, () => {
            this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT, {"slot": this.slot});
            if (this.progressTimer) clearTimeout(this.progressTimer);
        });
    }

    actionStatusCheckFailed() {
        this.cancel();
        this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
            dType: 'ERROR',
            dTitle: '',
            dProceedBtnLabel: '',
            msg: 'An error occurred processing your request.',
            onProceed: () => {
            }
        } as IAlertDialogData);
    }

    showStaleDataDialog() {
        this.cancel();
        this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
            dTitle: 'Refresh Required',
            dProceedBtnLabel: 'Refresh',
            msg: "You are attempting to edit items which were just modified by someone else. Please refresh page to see latest changes.",
            onProceed: () => {
                this.as.fireEvent(IrbAppEvents.GRID_REFRESH);
            }
        });
    }

    onSecurityTLHWashSaleEquivalentsChanged(events: any[]) {
        this.isValid = events.every(e => e.isValid);

        if (this.originalData && this.originalData.securityData && this.originalData.securityData.length > 0) {
            events.forEach((e: any, index: number) => {
                const wsas = e.tlhWashSaleAltSecuritiesDTO && e.tlhWashSaleAltSecuritiesDTO.length ? e.tlhWashSaleAltSecuritiesDTO : [];
                const sec = this.data.securityData[index];
                if (sec) {
                    if (e.isTlhEnabled !== null) {
                        sec.isTlhEnabled = e.isTlhEnabled;
            }
                    sec.tlhWashSaleAltSecuritiesDTO = wsas;
                }
            });
            this.onInputChange();
        }
    }
}
